import React, { useEffect, useRef, useCallback, useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useConfirmNavigation } from '../hooks/useUnsavedChanges';
import {
  AppBar, Box, Button, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, Alert, IconButton, Paper, TextField, Toolbar, Tooltip,
  Typography, CircularProgress,
} from '@mui/material';
import DashboardIcon        from '@mui/icons-material/Dashboard';
import DevicesIcon          from '@mui/icons-material/Devices';
import LogoutIcon           from '@mui/icons-material/Logout';
import VideocamIcon         from '@mui/icons-material/Videocam';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import PeopleIcon           from '@mui/icons-material/People';
import LoginIcon            from '@mui/icons-material/Login';
import SettingsIcon         from '@mui/icons-material/Settings';
import CoffeeIcon           from '@mui/icons-material/Coffee';
import LocationOnIcon       from '@mui/icons-material/LocationOn';
import DeleteSweepIcon      from '@mui/icons-material/DeleteSweep';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import AccessTimeIcon       from '@mui/icons-material/AccessTime';
import CheckCircleIcon      from '@mui/icons-material/CheckCircle';
import CancelIcon           from '@mui/icons-material/Cancel';
import AssessmentIcon       from '@mui/icons-material/Assessment';
import PersonSearchIcon     from '@mui/icons-material/PersonSearch';
import StorefrontIcon       from '@mui/icons-material/Storefront';
import BusinessIcon         from '@mui/icons-material/Business';
import ScheduleIcon         from '@mui/icons-material/Schedule';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import ScreenshotMonitorIcon from '@mui/icons-material/ScreenshotMonitor';
import {
  getUserRole, isAdmin, isSupervisor, isSuperAdmin, isVendorAdmin, isInfraAdmin,
  getVendorName, getVendorFeatures, getVendorId, getActivityPolicy,
  getPendingBreaks, approveBreak, rejectBreak,
} from '../api/client';

const REASON_LABEL = {
  coffee: '☕ Coffee', lunch: '🍱 Lunch', personal: '🚶 Personal',
  restroom: '🚻 Restroom', medical: '🏥 Medical', other: '⏸ Other',
};

// ── Break notification popup (supervisor only, shown on any page) ─────────────
function BreakNotificationPopup({ breakItem, onApprove, onReject, onDismiss }) {
  if (!breakItem) return null;
  return (
    <Dialog open maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1,
                         bgcolor: '#fff3e0', borderBottom: '1px solid #ffe0b2' }}>
        <NotificationsActiveIcon color="warning" />
        <Typography fontWeight={700}>New Break Request</Typography>
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Paper sx={{ p: 2, mb: 2, bgcolor: '#fff8f0', borderLeft: '4px solid #ff9800' }}>
          <Typography variant="h6" fontWeight={700}>{breakItem.operator_name}</Typography>
          <Typography variant="body2" color="text.secondary">
            Employee #{breakItem.operator_employee_id}
          </Typography>
          <Chip label={REASON_LABEL[breakItem.reason] || breakItem.reason}
            size="small" sx={{ mt: 1 }}
            color={breakItem.reason === 'medical' ? 'error' : 'warning'} />
          <Typography variant="caption" display="block" color="text.secondary" mt={1}>
            Requested at {breakItem.requested_at
              ? new Date(breakItem.requested_at).toLocaleTimeString() : '—'}
          </Typography>
        </Paper>
        <Alert severity="info" icon={<AccessTimeIcon />} sx={{ py: 0.5 }}>
          Approving starts the break after the configured delay for this operator.
        </Alert>
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 2, gap: 1 }}>
        <Button onClick={onDismiss} color="inherit" size="small">Later</Button>
        <Button onClick={() => onReject(breakItem)} color="error" variant="outlined" size="small">Reject</Button>
        <Button onClick={() => onApprove(breakItem)} color="success" variant="contained">Approve</Button>
      </DialogActions>
    </Dialog>
  );
}

function ApproveDialog({ open, onClose, breakItem, onDone }) {
  const [passkey, setPasskey] = useState('');
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState('');
  useEffect(() => { if (open) { setPasskey(''); setError(''); } }, [open]);
  const handle = async () => {
    setSaving(true); setError('');
    try {
      const result = await approveBreak(breakItem.id, passkey || undefined);
      const secs    = result.starts_in_seconds || 0;
      const mins    = result.delay_minutes      || 0;
      const msg     = secs <= 5
        ? 'Break approved — starts immediately.'
        : `Break approved — starts in ${mins} minute${mins !== 1 ? 's' : ''}.`;
      onDone && onDone(msg);
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Approval failed.');
    } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CheckCircleIcon color="success" /> Approve Break
      </DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Typography variant="body2" mb={2}>
          For <strong>{breakItem?.operator_name}</strong> · {REASON_LABEL[breakItem?.reason] || breakItem?.reason}
        </Typography>
        <Alert severity="info" icon={<AccessTimeIcon />} sx={{ mb: 2, py: 0.5 }}>
          Break starts after the configured delay.
        </Alert>
        {isSupervisor() && (
          <TextField label="Your supervisor PIN" type="password"
            inputProps={{ maxLength: 8, inputMode: 'numeric' }}
            value={passkey}
            onChange={e => setPasskey(e.target.value.replace(/\D/g, ''))}
            onKeyDown={e => e.key === 'Enter' && handle()}
            fullWidth size="small" autoFocus />
        )}
        {isAdmin() && (
          <Alert severity="success" sx={{ py: 0.5 }}>Admin approval — no PIN required.</Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={handle} variant="contained" color="success" disabled={saving}>
          {saving ? <CircularProgress size={20} /> : 'Approve'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function RejectDialog({ open, onClose, breakItem, onDone }) {
  const [passkey, setPasskey] = useState('');
  const [reason,  setReason]  = useState('');
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState('');
  useEffect(() => { if (open) { setPasskey(''); setReason(''); setError(''); } }, [open]);
  const handle = async () => {
    setSaving(true); setError('');
    try {
      await rejectBreak(breakItem.id, passkey || undefined, reason || undefined);
      onDone && onDone(`Break rejected.`);
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Rejection failed.');
    } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CancelIcon color="error" /> Reject Break
      </DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Typography variant="body2" mb={2}>
          Reject break for <strong>{breakItem?.operator_name}</strong>?
        </Typography>
        {isSupervisor() && (
          <TextField label="Your supervisor PIN" type="password"
            inputProps={{ maxLength: 8, inputMode: 'numeric' }}
            value={passkey} onChange={e => setPasskey(e.target.value.replace(/\D/g, ''))}
            fullWidth size="small" sx={{ mb: 2 }} autoFocus />
        )}
        <TextField label="Reason (shown to operator)"
          value={reason} onChange={e => setReason(e.target.value)}
          fullWidth size="small" multiline rows={2} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={handle} variant="contained" color="error" disabled={saving}>
          {saving ? <CircularProgress size={20} /> : 'Reject'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function AppLayout({ sseConnected, sseReconnecting }) {
  const navigate  = useNavigate();
  const location  = useLocation();
  const confirmNavigation = useConfirmNavigation();

  // ── Global break polling for supervisors ──
  const seenIds       = useRef(new Set());
  const [notifyBreak, setNotifyBreak]   = useState(null);
  const [approveItem, setApproveItem]   = useState(null);
  const [rejectItem,  setRejectItem]    = useState(null);
  const [pendingCount, setPendingCount] = useState(0);
  const [featurePermissions, setFeaturePermissions] = useState(null);
  // Supervisor's effective Work Activity policy (license AND vendor-admin's
  // own ActivityPolicy toggle, already ANDed server-side by
  // get_effective_policy — see services/activity_monitoring.py) — a
  // supervisor has no config page of their own, so "is this feature usable
  // right now" for their nav item can only be answered by the effective
  // policy, not the raw license alone (featurePermissions.screenshot only
  // tells you the vendor *could* enable it, not that they have).
  const [activityPolicy, setActivityPolicy] = useState(null);

  useEffect(() => {
    const vendorId = getVendorId();
    if (vendorId) getVendorFeatures(vendorId).then(setFeaturePermissions).catch(() => {});
    if (isSupervisor()) getActivityPolicy().then(setActivityPolicy).catch(() => {});
  }, []);

  const pollBreaks = useCallback(async () => {
    if (!isSupervisor()) return;
    try {
      const list = await getPendingBreaks();
      if (!Array.isArray(list)) return;
      setPendingCount(list.length);
      const unseen = list.find(b => !seenIds.current.has(b.id));
      if (unseen && !approveItem && !rejectItem) {
        seenIds.current.add(unseen.id);
        setNotifyBreak(unseen);
      }
    } catch (_) {}
  }, [approveItem, rejectItem]);

  useEffect(() => {
    if (!isSupervisor()) return;
    pollBreaks();
    const t = setInterval(pollBreaks, 10_000);
    return () => clearInterval(t);
  }, [pollBreaks]);

  const handleLogout = () => {
    confirmNavigation(() => {
      ['access_token', 'user_role', 'operator_id', 'location_id', 'vendor_id', 'vendor_name']
        .forEach(k => localStorage.removeItem(k));
      navigate('/login');
    });
  };

  const navBtn = (to, label, Icon, badge) => (
    <Button
      key={to}
      onClick={() => confirmNavigation(() => navigate(to))}
      color="inherit"
      startIcon={<Icon />}
      sx={{
        mx: 0.5, fontWeight: location.pathname === to ? 700 : 400,
        borderBottom: location.pathname === to ? '2px solid white' : 'none',
        borderRadius: 0, textTransform: 'none', fontSize: '0.82rem', position: 'relative',
      }}
    >
      {label}
      {badge > 0 && (
        <Box sx={{
          position: 'absolute', top: 2, right: 0,
          bgcolor: 'error.main', color: 'white',
          borderRadius: '50%', width: 16, height: 16,
          fontSize: 10, display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>{badge > 9 ? '9+' : badge}</Box>
      )}
    </Button>
  );

  // Super-admin only sees vendor management, no device/event data
  const superAdminNav = [
    navBtn('/vendors', 'Vendors', StorefrontIcon),
    navBtn('/vendor-features', 'Feature Permissions', SettingsIcon),
  ];

  const commonNav = [
    navBtn('/', 'Fleet', DevicesIcon),
    navBtn('/events', 'Events', DashboardIcon),
    navBtn('/live', 'Live', VideocamIcon),
    navBtn('/breaks', 'Breaks', CoffeeIcon, pendingCount),
  ];

  // Workforce group (Operators / Sessions / Presence Monitoring): available
  // to both vendor-admin and supervisor, never to operator (operators don't
  // get a dashboard login at all — see api/auth.py — so there's no role
  // check to add here beyond "not rendered under superAdminNav/infraAdminNav").
  // Presence Monitoring is additionally gated by the vendor's own
  // presence-feature entitlement (services/vendor_features.py), fetched
  // once above via getVendorFeatures — same entitlement check for both
  // roles now that api/vendor_features.py's GET allows supervisor reads,
  // not just administrator.
  const workforceNav = [
    navBtn('/operators', 'Operators', PeopleIcon),
    navBtn('/sessions', 'Sessions', LoginIcon),
    ...(featurePermissions?.presence ? [navBtn('/presence', 'Presence Monitoring', PersonSearchIcon)] : []),
  ];

  // Vendor-admin: full ops nav + locations/reports/settings, plus their own
  // vendor-scoped Retention page (editable — see RetentionPage.jsx).
  // "Supervisors" not "Operators" here — admins only create/manage
  // supervisor accounts; OperatorsPage shows the supervisor list with an
  // "Operators" count column per supervisor instead of individual
  // operators. (Same /operators route, page renders differently per role.)
  const vendorAdminNav = [
    ...workforceNav,
    navBtn('/shifts', 'Shifts', ScheduleIcon),
    navBtn('/locations', 'Locations', LocationOnIcon),
    navBtn('/reports', 'Reports', AssessmentIcon),
    navBtn('/alarm-config', 'Alarm Configuration', NotificationsActiveOutlinedIcon),
    // Only shown once the vendor is licensed for at least one Work Activity
    // feature (super-admin's VendorFeaturePermission) — otherwise there is
    // nothing this page would let a vendor-admin turn on.
    ...(featurePermissions?.screenshot || featurePermissions?.browser_history
      ? [navBtn('/activity-policy', 'Activity Policy', ScreenshotMonitorIcon)] : []),
    navBtn('/retention', 'Retention', DeleteSweepIcon),
    navBtn('/settings', 'Settings', SettingsIcon),
  ];

  // Infra-admin: retention (global default policy) + settings only
  const infraAdminNav = [
    navBtn('/retention', 'Retention', DeleteSweepIcon),
    navBtn('/settings', 'Settings', SettingsIcon),
  ];

  // Supervisor manages their own operators directly, and gets read-only
  // access to their vendor's Retention page (RetentionPage.jsx enforces
  // read-only client-side; the API enforces it server-side too).
  const supervisorNav = [
    ...workforceNav,
    navBtn('/shifts', 'Shifts', ScheduleIcon),
    navBtn('/reports', 'Reports', AssessmentIcon),
    // Same route as the vendor-admin's "Alarm Configuration" — AlarmConfigPage.jsx
    // itself renders the read-only-configuration + editable-assignment view for
    // supervisors instead of the full editable configuration view. Configuration
    // (vendor-admin) and Assignment (supervisor) are different responsibilities,
    // so they get different nav labels even though it's the same route.
    navBtn('/alarm-config', 'Device Assignment', NotificationsActiveOutlinedIcon),
    // Effective policy (license AND the vendor-admin's own toggle, already
    // resolved server-side) — a supervisor has no way to enable this
    // themselves, so the nav item (and the manual-capture buttons/history
    // it leads to) must disappear the moment either layer is off, not just
    // when the license is missing.
    ...(activityPolicy?.screenshot_enabled || activityPolicy?.browser_history_enabled
      ? [navBtn('/work-activity', 'Work Activity', ScreenshotMonitorIcon)] : []),
    navBtn('/retention', 'Retention', DeleteSweepIcon),
    navBtn('/settings', 'Settings', SettingsIcon),
  ];

  const connColor = sseConnected ? 'success' : sseReconnecting ? 'warning' : 'error';
  const connLabel = sseConnected ? 'Live' : sseReconnecting ? 'Reconnecting' : 'Disconnected';

  const roleLabel = isSuperAdmin()   ? 'Super-Admin'
                  : isVendorAdmin()  ? 'Admin'
                  : isInfraAdmin()   ? 'Infra-Admin'
                  : isSupervisor()   ? 'Supervisor'
                  : 'Viewer';
  const roleBadgeColor = isSuperAdmin()  ? '#7b1fa2'
                       : isVendorAdmin() ? '#ff6b35'
                       : isInfraAdmin()  ? '#1565c0'
                       : '#4caf50';

  const vendorName = getVendorName();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* Global break popup for supervisors — visible on any page */}
      {isSupervisor() && (
        <>
          <BreakNotificationPopup
            breakItem={notifyBreak}
            onApprove={brk => { setNotifyBreak(null); setApproveItem(brk); }}
            onReject={brk  => { setNotifyBreak(null); setRejectItem(brk);  }}
            onDismiss={()  => setNotifyBreak(null)}
          />
          <ApproveDialog open={!!approveItem} breakItem={approveItem}
            onClose={() => setApproveItem(null)}
            onDone={() => { setPendingCount(c => Math.max(0, c - 1)); pollBreaks(); }} />
          <RejectDialog open={!!rejectItem} breakItem={rejectItem}
            onClose={() => setRejectItem(null)}
            onDone={() => { setPendingCount(c => Math.max(0, c - 1)); pollBreaks(); }} />
        </>
      )}

      <AppBar position="static">
        <Toolbar variant="dense">
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mr: 1, whiteSpace: 'nowrap' }}>
            Drowsiness
          </Typography>
          <Chip label={roleLabel} size="small"
            sx={{ mr: vendorName ? 1 : 2, height: 20, fontSize: '0.7rem', fontWeight: 700,
                  bgcolor: roleBadgeColor, color: 'white' }} />
          {vendorName && (
            <Chip icon={<BusinessIcon sx={{ fontSize: 12 }} />}
              label={vendorName} size="small"
              sx={{ mr: 2, height: 20, fontSize: '0.7rem',
                    bgcolor: 'rgba(255,255,255,0.15)', color: 'white',
                    '& .MuiChip-icon': { color: 'white' } }} />
          )}

          {isSuperAdmin()  && superAdminNav}
          {isVendorAdmin() && [...commonNav, ...vendorAdminNav]}
          {isInfraAdmin()  && infraAdminNav}
          {isSupervisor()  && [...commonNav, ...supervisorNav]}

          <Box sx={{ flexGrow: 1 }} />

          <Tooltip title={`Live feed: ${connLabel}`}>
            <Chip icon={<FiberManualRecordIcon sx={{ fontSize: 10 }} />}
              label={connLabel} color={connColor} size="small" variant="outlined"
              sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.5)', mr: 2 }} />
          </Tooltip>

          <Tooltip title="Logout">
            <IconButton color="inherit" onClick={handleLogout} size="small">
              <LogoutIcon />
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>

      <Box component="main" sx={{ flexGrow: 1, bgcolor: '#f5f5f5' }}>
        <Outlet />
      </Box>
    </Box>
  );
}
